import { useEffect, useState, MouseEvent } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  Collapse,
  Divider,
  Drawer,
  DrawerProps,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import LockResetIcon from '@mui/icons-material/LockReset';
import AccountCircleIcon from '@mui/icons-material/AccountCircleOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../context/authContext';
import { Logo } from '../components/Logo';
import { useNotificationSummary } from '../hooks/useNotificationSummary';
import { isNavItemVisible, NAV_GROUPS, NAV_ITEMS, NavItem, visibleNavChildren } from './navItems';

const itemSx = (showLabels: boolean) => ({
  borderRadius: 2,
  justifyContent: showLabels ? 'flex-start' : 'center',
  px: showLabels ? 1.5 : 1,
  color: 'rgba(255,255,255,0.75)',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
  '&.Mui-selected': {
    bgcolor: 'rgba(139, 92, 246, 0.22)',
    color: '#fff',
    '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.28)' },
  },
});

export const SIDEBAR_WIDTH = 264;
export const SIDEBAR_WIDTH_COLLAPSED = 80;

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  variant: DrawerProps['variant'];
  open?: boolean;
  onClose?: () => void;
}

export const Sidebar = ({ collapsed, onToggleCollapsed, variant, open, onClose }: SidebarProps) => {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { data: notificationSummary } = useNotificationSummary();

  const isTemporary = variant === 'temporary';
  const showLabels = !collapsed || isTemporary;

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };

  const visibleItems = NAV_ITEMS.filter(
    (item) =>
      (!item.permission || hasPermission(item.permission.module, item.permission.action)) &&
      isNavItemVisible(item, user?.role.name),
  ).filter((item) => !item.children || visibleNavChildren(item, hasPermission).length > 0);

  // Submenus (e.g. Master Data): open while one of their pages is shown —
  // including when opened directly by URL — and toggled by the parent.
  const isUnder = (item: Pick<NavItem, 'path'>) => location.pathname.startsWith(item.path);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const active = NAV_ITEMS.find((item) => item.children && isUnder(item));
    if (active) setOpenMenus((prev) => (prev[active.path] ? prev : { ...prev, [active.path]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  // Icon-only sidebar: a submenu opens as a pop-out menu instead.
  const [flyout, setFlyout] = useState<{ anchor: HTMLElement; item: NavItem } | null>(null);

  return (
    <Drawer
      variant={variant}
      open={isTemporary ? open : true}
      onClose={onClose}
      ModalProps={isTemporary ? { keepMounted: true } : undefined}
      sx={{
        width: isTemporary ? SIDEBAR_WIDTH : collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        transition: (theme) => theme.transitions.create('width', { duration: 200 }),
        [`& .MuiDrawer-paper`]: {
          width: isTemporary ? SIDEBAR_WIDTH : collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: (theme) => theme.transitions.create('width', { duration: 200 }),
          bgcolor: '#160f26',
          color: 'rgba(255,255,255,0.92)',
          border: 'none',
          backgroundImage: 'linear-gradient(180deg, #1d1533 0%, #130d21 100%)',
        },
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={showLabels ? 'space-between' : 'center'}
          sx={{ px: showLabels ? 2.5 : 1, py: 2.5, minHeight: 72 }}
        >
          {showLabels && (
            <Box
              sx={{ bgcolor: 'rgba(255,255,255,0.94)', borderRadius: 1.5, px: 1.25, py: 0.5, display: 'flex' }}
            >
              <Logo height={26} />
            </Box>
          )}
          {!isTemporary && (
            <IconButton
              onClick={onToggleCollapsed}
              size="small"
              sx={{ color: 'rgba(255,255,255,0.7)', bgcolor: 'rgba(255,255,255,0.06)' }}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          )}
        </Stack>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', px: showLabels ? 1.5 : 1 }}>
          {NAV_GROUPS.map((group) => {
            const itemsInGroup = visibleItems.filter((item) => item.group === group);
            if (itemsInGroup.length === 0) return null;
            return (
              <Box key={group} sx={{ mb: 1.5 }}>
                {showLabels && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      px: 1.5,
                      pt: 1.5,
                      pb: 0.5,
                      color: 'rgba(255,255,255,0.45)',
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                    }}
                  >
                    {group}
                  </Typography>
                )}
                <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {itemsInGroup.map((item) => {
                    if (item.children) {
                      const children = visibleNavChildren(item, hasPermission);
                      const active = isUnder(item);
                      const expanded = !!openMenus[item.path];
                      const parent = (
                        <ListItemButton
                          key={item.path}
                          selected={active && !expanded}
                          aria-expanded={showLabels ? expanded : undefined}
                          aria-haspopup={showLabels ? undefined : 'menu'}
                          onClick={(e) =>
                            showLabels
                              ? setOpenMenus((prev) => ({ ...prev, [item.path]: !prev[item.path] }))
                              : setFlyout({ anchor: e.currentTarget, item })
                          }
                          sx={itemSx(showLabels)}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: showLabels ? 36 : 'auto',
                              color: active ? 'secondary.light' : 'rgba(255,255,255,0.55)',
                              justifyContent: 'center',
                            }}
                          >
                            <item.icon fontSize="small" />
                          </ListItemIcon>
                          {showLabels && (
                            <>
                              <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 600 : 500 }}
                              />
                              {expanded ? (
                                <ExpandLessIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.55)' }} />
                              ) : (
                                <ExpandMoreIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.55)' }} />
                              )}
                            </>
                          )}
                        </ListItemButton>
                      );
                      if (!showLabels) {
                        return (
                          <Tooltip key={item.path} title={item.label} placement="right">
                            {parent}
                          </Tooltip>
                        );
                      }
                      return (
                        <Box key={item.path}>
                          {parent}
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <List
                              dense
                              disablePadding
                              aria-label={`${item.label} pages`}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25,
                                mt: 0.25,
                                ml: 2.25,
                                pl: 1,
                                borderLeft: '1px solid rgba(255,255,255,0.1)',
                              }}
                            >
                              {children.map((child) => {
                                const childSelected = location.pathname.startsWith(child.path);
                                return (
                                  <ListItemButton
                                    key={child.path}
                                    component={RouterLink}
                                    to={child.path}
                                    selected={childSelected}
                                    aria-current={childSelected ? 'page' : undefined}
                                    onClick={isTemporary ? onClose : undefined}
                                    sx={{ ...itemSx(true), py: 0.4 }}
                                  >
                                    <ListItemText
                                      primary={child.label}
                                      primaryTypographyProps={{
                                        fontSize: '0.8125rem',
                                        fontWeight: childSelected ? 600 : 400,
                                        noWrap: true,
                                      }}
                                    />
                                  </ListItemButton>
                                );
                              })}
                            </List>
                          </Collapse>
                        </Box>
                      );
                    }
                    const selected =
                      item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
                    // Role-based notification/pending-action indicator
                    // system: badgeCount is null both when the item has no
                    // badgeKey at all AND when the summary hasn't loaded
                    // yet/the caller lacks permission for that field
                    // (summary returns null there, never 0) — either way,
                    // no badge renders, matching §17's "no badge at 0/none".
                    const badgeCount = item.badgeKey ? notificationSummary?.[item.badgeKey] ?? null : null;
                    const button = (
                      <ListItemButton
                        key={item.path}
                        component={RouterLink}
                        to={item.path}
                        selected={selected}
                        onClick={isTemporary ? onClose : undefined}
                        sx={{
                          borderRadius: 2,
                          justifyContent: showLabels ? 'flex-start' : 'center',
                          px: showLabels ? 1.5 : 1,
                          color: 'rgba(255,255,255,0.75)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                          '&.Mui-selected': {
                            bgcolor: 'rgba(139, 92, 246, 0.22)',
                            color: '#fff',
                            '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.28)' },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: showLabels ? 36 : 'auto',
                            color: selected ? 'secondary.light' : 'rgba(255,255,255,0.55)',
                            justifyContent: 'center',
                          }}
                        >
                          {badgeCount ? (
                            <Badge badgeContent={badgeCount} color="error" max={99} overlap="circular">
                              <item.icon fontSize="small" />
                            </Badge>
                          ) : (
                            <item.icon fontSize="small" />
                          )}
                        </ListItemIcon>
                        {showLabels && (
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: selected ? 600 : 500 }}
                          />
                        )}
                      </ListItemButton>
                    );
                    return showLabels ? (
                      button
                    ) : (
                      <Tooltip key={item.path} title={item.label} placement="right">
                        {button}
                      </Tooltip>
                    );
                  })}
                </List>
              </Box>
            );
          })}
        </Box>

        <Menu
          anchorEl={flyout?.anchor}
          open={!!flyout}
          onClose={() => setFlyout(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { maxHeight: '80vh' } } }}
        >
          {flyout && (
            <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 700 }}>
              {flyout.item.label}
            </Typography>
          )}
          {flyout &&
            visibleNavChildren(flyout.item, hasPermission).map((child) => (
              <MenuItem
                key={child.path}
                component={RouterLink}
                to={child.path}
                selected={location.pathname.startsWith(child.path)}
                onClick={() => setFlyout(null)}
              >
                {child.label}
              </MenuItem>
            ))}
        </Menu>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        <Box sx={{ p: showLabels ? 1.5 : 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            onClick={handleMenuOpen}
            sx={{
              cursor: 'pointer',
              borderRadius: 2,
              p: 1,
              justifyContent: showLabels ? 'flex-start' : 'center',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'secondary.main', fontSize: 14 }}>
              {user ? `${user.firstName[0]}${user.lastName[0]}` : '?'}
            </Avatar>
            {showLabels && (
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: '#fff' }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                  {user?.role.name}
                </Typography>
              </Box>
            )}
          </Stack>
          <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/my-account');
              }}
            >
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              My Account
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/change-password');
              }}
            >
              <ListItemIcon>
                <LockResetIcon fontSize="small" />
              </ListItemIcon>
              Change password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Stack>
    </Drawer>
  );
};
